import type { ChatState } from "../types"
import type { Jid } from "../xmpp/jid"

/** A pause this long in typing says `paused`. */
export const IDLE_MS = 5_000

/** Typing on re-says `composing` this often, well inside the receiver's expiry. */
export const REFRESH_MS = 15_000

/**
 * Decides what our typing puts on the wire (XEP-0085), so a burst of keystrokes
 * costs one stanza, not one each.
 */
export function createTypingNotifier(
  send: (peer: Jid, state: ChatState) => void
) {
  let composing: { peer: Jid; sentAt: number } | undefined
  let idle: ReturnType<typeof setTimeout> | undefined

  const stop = (sayPaused: boolean) => {
    clearTimeout(idle)
    if (composing && sayPaused) send(composing.peer, "paused")
    composing = undefined
  }

  return {
    draftChanged(peer: Jid, draft: string) {
      if (draft.trim() === "") return stop(true)

      const now = Date.now()
      if (!composing || now - composing.sentAt > REFRESH_MS) {
        send(peer, "composing")
        composing = { peer, sentAt: now }
      }

      clearTimeout(idle)
      idle = setTimeout(() => stop(true), IDLE_MS)
    },

    /** The message itself ends the indicator at the other end. */
    sent: () => stop(false),

    /** Leaving the conversation mid-sentence stops us typing in it. */
    left: () => stop(true),
  }
}
